import { createRouter, createWebHistory } from 'vue-router'
import LandingPage from '@/pages/LandingPage.vue'
import TaxRefundOptions from '@/pages/TaxRefundOptions.vue'
import TaxRefundQuestions from '@/pages/TaxRefundQuestions.vue'
import MedicalInsuranceRelief from '@/pages/form11/MedicalInsuranceRelief.vue'
import Questionnaire from '@/pages/form11/Questionnaire.vue'
import IncomeSelection from '@/pages/form11/IncomeSelection.vue'
import FinalForm from '@/pages/form11/FinalForm.vue'
import WelcomePage from '@/pages/auth/WelcomePage.vue'
import AuthPage from '@/pages/auth/AuthPage.vue'
import LoginPage from '@/pages/auth/LoginPage.vue'
import RegisterPage from '@/pages/auth/RegisterPage.vue'
import PayeAuthorization from '@/pages/auth/PayeAuthorization.vue'
import TermsAndConditions from '@/pages/TermsAndConditions.vue'
import { useAuth } from '@/composables/useAuth'

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', redirect: '/landing' },
    { path: '/landing', component: LandingPage },

    // Profile route
    {
      path: '/profile',
      component: () => import('@/pages/ProfilePage.vue'),
    },

    // Settings route
    {
      path: '/settings',
      component: () => import('@/pages/SettingsPage.vue'),
    },

    // Contribution routes
    {
      path: '/contribute',
      component: () => import('@/pages/contribute/ContributePage.vue'),
    },
    {
      path: '/direct-debit',
      component: () => import('@/pages/contribute/DirectDebitPage.vue'),
    },
    {
      path: '/round-payment',
      component: () => import('@/pages/contribute/RoundPaymentPage.vue'),
    },

    // Tax routes
    {
      path: '/tax-refund-options',
      component: TaxRefundOptions,
    },
    {
      path: '/tax-refund-questions',
      component: TaxRefundQuestions,
    },
    {
      path: '/form11-questionnaire',
      name: 'Questionnaire',
      component: Questionnaire,
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/income-selection',
      name: 'IncomeSelection',
      component: IncomeSelection,
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/final-form',
      name: 'FinalForm',
      component: FinalForm,
      meta: { requiresPayeAuth: true },
    },

    // Auth routes
    { path: '/welcome', component: WelcomePage, meta: { isPublic: true } },
    { path: '/auth', component: AuthPage, meta: { isPublic: true } },
    { path: '/auth/login', component: LoginPage, meta: { isPublic: true } },
    {
      path: '/auth/register',
      component: RegisterPage,
      meta: { isPublic: true },
    },
    {
      path: '/auth/forgot-password',
      component: () => import('@/pages/auth/ForgotPasswordPage.vue'),
      meta: { isPublic: true },
    },
    {
      path: '/reset-password/:token',
      component: () => import('@/pages/auth/ResetPasswordPage.vue'),
      meta: { isPublic: true },
    },
    {
      path: '/auth/paye-authorization',
      component: PayeAuthorization,
    },

    // Health routes
    {
      path: '/form11/assistance-dog-allowance',
      component: () => import('@/pages/form11/AssistanceDogAllowance.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/permanent-health-benefit',
      component: () => import('@/pages/form11/PermanentHealthBenefit.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/guide-dog-allowance',
      component: () => import('@/pages/form11/GuideDogAllowance.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/income-continuance',
      component: () => import('@/pages/form11/IncomeContinuance.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/nursing-home-expenses',
      component: () => import('@/pages/form11/NursingHomeExpenses.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/blind-person-tax-credit',
      component: () => import('@/pages/form11/BlindPersonTaxCredit.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/health-expenses',
      component: () => import('@/pages/form11/HealthExpenses.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/medical-insurance-relief',
      name: 'MedicalInsuranceRelief',
      component: MedicalInsuranceRelief,
      meta: { requiresPayeAuth: true },
    },

    // Family routes
    {
      path: '/form11/dependant-relevant-tax-credit',
      component: () => import('@/pages/form11/DependantRelevantTaxCredit.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/incapacitated-child-tax-credit',
      component: () => import('@/pages/form11/IncapacitatedChildTaxCredit.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/maintenance-payment-relief',
      component: () => import('@/pages/form11/MaintenancePaymentRelief.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/single-person-child-carer-credit',
      component: () =>
        import('@/pages/form11/SinglePersonChildCarerCredit.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/rent-tax-credit',
      component: () => import('@/pages/form11/RentTaxCredit.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/tuition-fees',
      component: () => import('@/pages/form11/TuitionFees.vue'),
      meta: { requiresPayeAuth: true },
    },

    // Job routes
    {
      path: '/form11/additional-voluntary-contributions',
      component: () =>
        import('@/pages/form11/AdditionalVoluntaryContributions.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/flat-rate-expenses',
      component: () => import('@/pages/form11/FlatRateExpenses.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/personal-retirement-savings',
      component: () => import('@/pages/form11/PersonalRetirementSavings.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/remote-working-relief',
      component: () => import('@/pages/form11/RemoteWorkingRelief.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/retirement-annuity-relief',
      component: () => import('@/pages/form11/RetirementAnnuityRelief.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/fisher-tax-credit',
      component: () => import('@/pages/form11/FisherTaxCredit.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/seafarer-allowance',
      component: () => import('@/pages/form11/SeafarerAllowance.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/naval-service-credit',
      component: () => import('@/pages/form11/NavalServiceCredit.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/owner-occupier-relief',
      component: () => import('@/pages/form11/OwnerOccupierRelief.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/home-renovation-incentive',
      component: () => import('@/pages/form11/HomeRenovationIncentive.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/approved-sports-bodies',
      component: () => import('@/pages/form11/ApprovedSportsBodies.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/startup-relief-entrepreneurs',
      component: () => import('@/pages/form11/StartupReliefEntrepreneurs.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/employment-investment-incentive',
      component: () =>
        import('@/pages/form11/EmploymentInvestmentIncentive.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/mortgage-interest-tax-credit',
      component: () => import('@/pages/form11/MortgageInterestTaxCredit.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/employee-tax-credit',
      component: () => import('@/pages/form11/EmployeeTaxCredit.vue'),
      meta: { requiresPayeAuth: true },
    },
    {
      path: '/form11/earned-income-tax-credit',
      component: () => import('@/pages/form11/EarnedIncomeTaxCredit.vue'),
      meta: { requiresPayeAuth: true },
    },
    // Pension routes
    {
      path: '/pension-info',
      component: () => import('@/pages/PensionInfo.vue'),
    },
    {
      path: '/pension-refund',
      component: () => import('@/pages/PensionRefund.vue'),
    },
    {
      path: '/linked-bank-accounts',
      name: 'LinkedBankAccounts',
      component: () => import('./pages/LinkedBankAccounts.vue'),
    },
    {
      path: '/bank-link/:url',
      name: 'BankLink',
      component: () => import('./pages/BankLink.vue'),
    },
    {
      path: '/terms-and-conditions',
      component: TermsAndConditions,
      meta: { isPublic: true },
    },
    {
      path: '/tax-refund-submitted',
      component: () => import('@/pages/TaxRefundSubmitted.vue'),
    },
  ],
})

// Add a router guard to check authentication
router.beforeEach(async (to, _from, next) => {
  const { user, loading, initAuth } = useAuth()

  // Wait for auth to initialize if it's loading
  if (loading.value) {
    await initAuth()
  }

  // Check if the route is public
  const isPublic = to.matched.some((record) => record.meta.isPublic)

  // Check if the route requires PAYE A1 authorization
  const requiresPayeAuth = to.matched.some(
    (record) => record.meta.requiresPayeAuth,
  )

  // Allow access to public routes without authentication
  if (isPublic) {
    return next()
  }

  // Handle authentication redirects for non-public routes (requires auth by default)
  if (!user.value) {
    // Redirect to login if trying to access protected route
    console.log(`Redirecting to login, we're not authenticated`)
    next('/auth/login')
    return
  }

  // Redirect to pension info if user is authenticated but has no annual income set
  if (user.value && !user.value?.annual_income && to.path !== '/pension-info') {
    console.log(`Redirecting to pension info, user has no annual income`)
    next('/pension-info')
    return
  }

  // Handle PAYE A1 authorization
  if (requiresPayeAuth && !user.value.paye_a1_authorized) {
    // Redirect to PAYE authorization if trying to access tax-related routes without authorization
    next({ path: '/auth/paye-authorization', query: { returnTo: to.path } })
    return
  }

  // Allow navigation
  next()
})
